/**
 * ウォーターマーク設定管理用のカスタムフック
 * 設定変更の監視、再適用、トグル処理を共通化
 */

import { useEffect, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { ThunkDispatch } from '@reduxjs/toolkit';
import { reapplyWatermarksOnSettingsChange } from '../utils/watermarkHelpers';
import { toastActions } from '../reducers/toast';

interface UseWatermarkSettingsProps {
  savedTimestamp?: number;
  lastProcessedTimestamp: number | null;
  setLastProcessedTimestamp: (timestamp: number) => void;
  watermarkGlobalEnabled: boolean;
  selectedAfterImages: any[];
  watermarkSettings: any;
  hasWatermarkSettings: boolean;
  clearAllWatermarksAction: any;
  applyWatermarkToImageAction: any;
  setWatermarkGlobalEnabledAction: any;
  toggleAllWatermarksAction: any;
}

/**
 * ウォーターマーク設定の監視と適用を管理するフック
 * 
 * @param {UseWatermarkSettingsProps} props - フックのパラメータ
 * @returns {Object} ウォーターマーク操作関数
 */
export const useWatermarkSettings = ({
  savedTimestamp,
  lastProcessedTimestamp,
  setLastProcessedTimestamp,
  watermarkGlobalEnabled,
  selectedAfterImages,
  watermarkSettings,
  hasWatermarkSettings,
  clearAllWatermarksAction,
  applyWatermarkToImageAction,
  setWatermarkGlobalEnabledAction,
  toggleAllWatermarksAction,
}: UseWatermarkSettingsProps) => {
  const dispatch = useDispatch<ThunkDispatch<any, any, any>>();

  // serviceSettingsのlastSavedTimestampを監視して、設定保存を検知
  useEffect(() => {
    // 初回レンダリング時や、タイムスタンプがない場合はスキップ
    if (!savedTimestamp || savedTimestamp === lastProcessedTimestamp) {
      return;
    }
    
    // 新しい保存が検知された場合
    reapplyWatermarksOnSettingsChange({
      watermarkGlobalEnabled,
      selectedAfterImages,
      watermarkSettings,
      dispatch,
      clearAllWatermarksAction,
      applyWatermarkToImageAction,
      setWatermarkGlobalEnabledAction
    });
    
    // 処理済みのタイムスタンプを記録
    setLastProcessedTimestamp(savedTimestamp);
  }, [savedTimestamp, lastProcessedTimestamp, watermarkGlobalEnabled, selectedAfterImages, watermarkSettings, dispatch]);

  // ウォーターマーク設定がある場合はデフォルトでONにする
  useEffect(() => {
    if (hasWatermarkSettings && !watermarkGlobalEnabled && selectedAfterImages.length > 0) {
      // ウォーターマークをONにして適用
      dispatch(toggleAllWatermarksAction(true));
    }
  }, [hasWatermarkSettings, selectedAfterImages.length, dispatch]);

  // ウォーターマークトグルのハンドラー
  const handleWatermarkToggle = useCallback(async (enabled: boolean, onShowModal: () => void) => {
    if (!hasWatermarkSettings && enabled) {
      dispatch(toastActions.toastWarning('ウォーターマーク設定が必要です'));
      onShowModal();
      return;
    }

    await dispatch(toggleAllWatermarksAction(enabled));
  }, [dispatch, hasWatermarkSettings, toggleAllWatermarksAction]);

  return {
    handleWatermarkToggle,
  };
};